import type {
  BookingStatusBreakdownItem,
  CourtOccupancyItem,
  OperationalInsight,
  RevenueSeriesPoint,
  VenueDashboardFilterParams,
  VenueDashboardSnapshot,
  VenueDashboardSummary,
} from "@/types/venue-analytics";

import { callEdgeData } from "./_core/edge-call";
import { VENUE_ANALYTICS_FUNCTION_NAME } from "./_core/function-names";

function buildVenueDashboardParams(filters: VenueDashboardFilterParams = {}): Record<string, string> {
  const params: Record<string, string> = {};

  if (filters.venue_id) params.venue_id = filters.venue_id;
  if (filters.range_key) params.range_key = filters.range_key;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.court_id) params.court_id = filters.court_id;
  if (filters.sport_id) params.sport_id = filters.sport_id;
  if (filters.booking_status) params.booking_status = filters.booking_status;

  return params;
}

async function getVenueDashboardSnapshot(
  filters: VenueDashboardFilterParams = {},
): Promise<VenueDashboardSnapshot> {
  return callEdgeData<VenueDashboardSnapshot>(VENUE_ANALYTICS_FUNCTION_NAME, "dashboard", {
    method: "GET",
    params: buildVenueDashboardParams(filters),
  });
}

/**
 * All analytics data comes from a single snapshot endpoint.
 * Prefer getDashboardSnapshot in new consumers and destructure what they need.
 */
export const venueAnalyticsService = {
  async getDashboardSnapshot(filters: VenueDashboardFilterParams = {}): Promise<VenueDashboardSnapshot> {
    return getVenueDashboardSnapshot(filters);
  },

  async getDashboardSummary(filters: VenueDashboardFilterParams = {}): Promise<VenueDashboardSummary> {
    return (await getVenueDashboardSnapshot(filters)).summary;
  },

  async getRevenueSeries(filters: VenueDashboardFilterParams = {}): Promise<RevenueSeriesPoint[]> {
    return (await getVenueDashboardSnapshot(filters)).revenue_series;
  },

  async getBookingStatusBreakdown(
    filters: VenueDashboardFilterParams = {},
  ): Promise<BookingStatusBreakdownItem[]> {
    return (await getVenueDashboardSnapshot(filters)).booking_status_breakdown;
  },

  async getCourtOccupancy(filters: VenueDashboardFilterParams = {}): Promise<CourtOccupancyItem[]> {
    return (await getVenueDashboardSnapshot(filters)).court_occupancy;
  },

  async getPeakHours(filters: VenueDashboardFilterParams = {}): Promise<VenueDashboardSnapshot["peak_hours"]> {
    return (await getVenueDashboardSnapshot(filters)).peak_hours;
  },

  async getOperationalInsights(filters: VenueDashboardFilterParams = {}): Promise<OperationalInsight[]> {
    return (await getVenueDashboardSnapshot(filters)).insights;
  },
};
